import './Switch.css'
import { type Dispatch, type HTMLAttributes, type ReactNode, type SetStateAction, useId } from 'react'
import { cn } from '../../utilities/misc'
import { Tooltip } from '../action/Tooltip'
import { type Placement } from '@floating-ui/dom'

type Props = HTMLAttributes<HTMLInputElement> & {
  label?: string
  className?: string
  tooltip?: ReactNode
  tooltipPlacement?: Placement
  disabled?: boolean
  defaultValue?: boolean
  value?: boolean
  setValue?: Dispatch<SetStateAction<boolean>>,
  onCheckedChange?: (checked: boolean) => void
}

/** Basic toggle switch component */
export function Switch({
  value,
  setValue,
  defaultValue,
  disabled = false,
  onCheckedChange,
  label,
  className,
  tooltip,
  tooltipPlacement,
  id,
  ...rest
}: Props) {
  const generatedId = useId()
  const switchId = id || generatedId

  //--------------------------------------------------------------------------------
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    setValue?.(e.target.checked)
    onCheckedChange?.(e.target.checked)
    rest.onChange?.(e)
  }

  //--------------------------------------------------------------------------------
  return (
    <label
      htmlFor={switchId}
      className={cn(
        'Switch',
        disabled && 'Disabled',
        className,
      )}
    >
      {!disabled && (
        <Tooltip placement={tooltipPlacement}>
          {tooltip}
        </Tooltip>
      )}
      <input
        type='checkbox'
        id={switchId}
        checked={value ?? false}
        defaultChecked={defaultValue}
        disabled={disabled}
        onChange={handleChange}
        className='Input'
        {...rest}
      />
      <span className='Track'>
        <span className='Thumb' />
      </span>
      {label && <span className='Label'>{label}</span>}
    </label>
  )
}
